import React, { useEffect, useState } from 'react';
import './App.css';
import AppHeader from './components/AppHeader';
import Logo from './components/Logo';
import ServiceStatus from './components/ServiceStatus';
import SubscriptionButton from './components/SubscriptionButton';
import SetupButton from './components/SetupButton';
import HomeReferralButton from './components/HomeReferralButton';
import ProfileButton from './components/ProfileButton';
import SupportButton from './components/SupportButton';
import ProfilePage from './components/ProfilePage';
import AdminPage from './components/AdminPage';
import PaymentPage from './components/PaymentPage';
import SetupFlow from './components/SetupFlow';
import TariffSelectionPage from './components/TariffSelectionPage';
import SubscriptionSelectionPage from './components/SubscriptionSelectionPage';
import PaymentReturnPage from './components/PaymentReturnPage';
import ReferralModal from './components/ReferralModal';
import { initTelegramSDK, getTelegramUser, getReferrerIdFromLaunch, getStartParam, showBackButton, hideBackButton, isInsideTelegram } from './services/telegram';
import apiService from './services/api';

// UI fallback for the active production administrators. This only controls
// whether the menu entry is visible; every admin API remains server-protected.
const KNOWN_ADMIN_IDS = new Set([
  284939715,
  696361073,
  1065290148,
  5088037387,
  5524989981,
  6609543726,
]);

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [subscribeTariff, setSubscribeTariff] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentReturnStatus, setPaymentReturnStatus] = useState(null);
  const [isHomeReferralOpen, setIsHomeReferralOpen] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [friendsInvited, setFriendsInvited] = useState(0);
  const [referralRewards, setReferralRewards] = useState([]);
  const [isReferralLoading, setIsReferralLoading] = useState(false);
  const [notInTelegram, setNotInTelegram] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showInstallHint, setShowInstallHint] = useState(false);

  const miniAppLink = process.env.REACT_APP_TELEGRAM_MINIAPP_LINK || 'https://t.me/rocket_fastnet_bot';
  const envBotUsername = (process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '').replace(/^@/, '').trim();
  const linkBotUsername = miniAppLink.replace(/^https?:\/\/t\.me\//, '').replace(/^@/, '').trim();
  const botUsername = envBotUsername || linkBotUsername || 'rocket_fastnet_bot';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = (window.location.pathname || '').replace(/\/$/, '');
    const hash = window.location.hash || '';
    if (path === '/success' || path === '/success/' || hash === '#success') {
      setCurrentPage('success');
      window.history.replaceState({}, '', window.location.origin + '/');
      return;
    }
    const refMatch = path.match(/^\/ref\/(\d+)$/);
    if (refMatch) {
      const referrerId = refMatch[1];
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('beznet_referrer_userid', referrerId);
      }
      window.history.replaceState({}, '', window.location.origin + '/');
    }
  }, []);


  useEffect(() => {
    if (currentPage !== 'success') return;
    setPaymentReturnStatus('loading');
    let cancelled = false;
    const run = async () => {
      let paymentId = null;
      try {
        paymentId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('beznet_pending_payment_id') : null;
        if (paymentId) {
          if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('beznet_pending_payment_id');
          const data = await apiService.getPaymentStatus(paymentId);
          const status = (data && data.status) ? String(data.status).toLowerCase() : '';
          if (!cancelled) setPaymentReturnStatus(
            status === 'succeeded' ? 'succeeded'
            : (status === 'canceled' || status === 'cancelled') ? 'canceled'
            : 'pending'
          );
        } else {
          if (!cancelled) setPaymentReturnStatus('unknown');
        }
      } catch (_) {
        if (!cancelled) setPaymentReturnStatus('unknown');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== 'success' || !user?.userid) return;
    apiService.getUser(user.userid).then(setUser).catch(() => {});
  }, [currentPage, user?.userid]);

  useEffect(() => {
    if (!user?.userid) return;
    let cancelled = false;
    const knownAdmin = KNOWN_ADMIN_IDS.has(Number(user.userid));
    if (knownAdmin) setIsAdmin(true);
    const checkAdmin = async () => {
      for (const waitMs of [0, 800, 2500]) {
        if (waitMs) await new Promise((resolve) => setTimeout(resolve, waitMs));
        if (cancelled) return;
        try {
          const res = await apiService.adminMe();
          if (res?.is_admin) {
            if (!cancelled) setIsAdmin(true);
            return;
          }
        } catch (_) {}
      }
      if (!cancelled) setIsAdmin(knownAdmin);
    };
    checkAdmin();
    const recheckOnVisible = () => {
      if (document.visibilityState === 'visible') checkAdmin();
    };
    document.addEventListener('visibilitychange', recheckOnVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', recheckOnVisible);
    };
  }, [user?.userid]);

  useEffect(() => {
    if (!user?.userid) return;
    const onVisible = () => {
      apiService.getUser(user.userid).then(setUser).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?.userid]);

  useEffect(() => {
    const uid = user?.userid ?? user?.id;
    if (!isHomeReferralOpen || !uid) return;
    // Funnel 4: user opened referral section
    apiService.trackEvent({ funnel_id: 4, step: 'referral_program_viewed', user_id: uid, username: user?.username ?? null });
    let cancelled = false;
    setIsReferralLoading(true);
    (async () => {
      try {
        const [linkRes, countRes, rewardsRes] = await Promise.allSettled([
          apiService.getReferralLink(String(uid)),
          apiService.getReferralCount(String(uid)),
          apiService.getReferralRewards(String(uid)),
        ]);
        if (cancelled) return;
        setReferralLink(linkRes.status === 'fulfilled' ? (linkRes.value?.referral_link || '') : '');
        setFriendsInvited(countRes.status === 'fulfilled' ? (Number(countRes.value?.referral_count) || 0) : 0);
        setReferralRewards(
          rewardsRes.status === 'fulfilled' && Array.isArray(rewardsRes.value?.rewards)
            ? rewardsRes.value.rewards
            : []
        );
      } catch (_) {
        if (cancelled) return;
        setReferralLink('');
        setFriendsInvited(0);
        setReferralRewards([]);
      } finally {
        if (!cancelled) setIsReferralLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setIsReferralLoading(false);
    };
  }, [isHomeReferralOpen, user?.userid, user?.id, user?.username]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsLoading(true);
        
        const telegramData = await initTelegramSDK();
        if (!telegramData) {
          console.warn('Telegram SDK not available');
          setNotInTelegram(true);
          setIsLoading(false);
          return;
        }
        const referrerId = getReferrerIdFromLaunch();
        if (referrerId && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('beznet_referrer_userid', referrerId);
        }

        const telegramUser = getTelegramUser();
        const fallbackUser = telegramUser ? {
          userid: Number(telegramUser.id),
          id: telegramUser.id,
          first_name: telegramUser.firstName || '',
          last_name: telegramUser.lastName || null,
          username: telegramUser.username || null,
          tariff_plan: null,
          tariff_end_date: null,
        } : null;

        if (!telegramUser) {
          console.warn('Telegram user data not available');
          setNotInTelegram(true);
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Handle deep-link start_param for direct navigation
        const startParam = getStartParam();

        try {
          const existingUser = await apiService.getUser(telegramUser.id);
          setUser(existingUser);
          // Navigate to the requested screen after user is loaded
          if (startParam === 'buy') {
            setSubscribeTariff('pro');
            setCurrentPage('subscription');
          } else if (startParam === 'referral') {
            setIsHomeReferralOpen(true);
          }
        } catch (error) {
          const isUserNotFound = error.status === 404
            || /not found|user not found|пользователь не найден/i.test(error.message || '');
          if (isUserNotFound) {
            try {
              const referrerId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('beznet_referrer_userid') : null;
              const createPayload = {
                userid: Number(telegramUser.id),
                first_name: telegramUser.firstName || '',
                last_name: telegramUser.lastName || null,
                username: telegramUser.username || null,
                tariff_plan: null,
                tariff_end_date: null,
              };
              if (referrerId) {
                const rid = parseInt(referrerId, 10);
                if (!Number.isNaN(rid) && rid !== Number(telegramUser.id)) {
                  createPayload.referrer_userid = rid;
                  sessionStorage.removeItem('beznet_referrer_userid');
                }
              }
              const newUser = await apiService.createUser(createPayload);
              setUser(newUser);
              if (startParam === 'buy') {
                setSubscribeTariff('pro');
                setCurrentPage('subscription');
              } else if (startParam === 'referral') {
                setIsHomeReferralOpen(true);
              }
            } catch (createError) {
              console.error('Error creating user:', createError);
              setUser(fallbackUser);
            }
          } else {
            console.error('Error fetching user:', error);
            setUser(fallbackUser);
          }
        }
      } catch (error) {
        console.error('App initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    if (isLoading) return;
    try {
      // localStorage persists across Telegram mini-app reopens; sessionStorage was
      // cleared on every relaunch so the hint kept appearing each time.
      const visited = typeof localStorage !== 'undefined' ? localStorage.getItem('rocket_visited') : '1';
      if (!visited) {
        localStorage.setItem('rocket_visited', '1');
        setShowInstallHint(true);
      }
    } catch (_) {}
  }, [isLoading]);

  useEffect(() => {
    if (currentPage === 'home') {
      hideBackButton();
      return;
    }
    if (currentPage === 'profile') {
      showBackButton(() => setCurrentPage('home'));
      return;
    }
    if (currentPage === 'admin') {
      showBackButton(() => setCurrentPage('profile'));
      return;
    }

    if (currentPage === 'payment') {
      showBackButton(() => setCurrentPage('profile'));
      return;
    }
    if (currentPage === 'subscription') {
      showBackButton(() => {
        setSubscribeTariff(null);
        setCurrentPage('home');
      });
      return;
    }
    if (currentPage === 'setup' || currentPage === 'success') {
      showBackButton(() => setCurrentPage('home'));
    }
    return () => hideBackButton();
  }, [currentPage]);

  if (notInTelegram) {
    return (
      <div className="app">
        <div className="background" />
        <div className="content" style={{ justifyContent: 'center', alignItems: 'center', padding: 24, textAlign: 'center' }}>
          <p style={{ color: '#fff', marginBottom: 16 }}>Это мини-приложение работает только внутри Telegram.</p>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8 }}>
            Откройте бота @{botUsername} и запустите мини-приложение оттуда.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>
            Если бот ещё не активирован, нажмите /start в чате с ботом.
          </p>
        </div>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="app">
        <div className="background"></div>
        <AppHeader title="Rocket VPN" />
        <div className="content">
          <Logo />
        </div>
      </div>
    );
  }

  if (currentPage === 'profile') {
    return (
      <ProfilePage
        user={user}
        onBack={() => setCurrentPage('home')}
        onNavigateToPayment={() => setCurrentPage('payment')}
        onOpenSetup={() => setCurrentPage('setup')}
        isAdmin={isAdmin}
        onOpenAdmin={() => setCurrentPage('admin')}
      />
    );
  }

  if (currentPage === 'admin') {
    if (!isAdmin) {
      setCurrentPage('profile');
      return null;
    }
    return <AdminPage user={user} onBack={() => setCurrentPage('profile')} />;
  }

  if (currentPage === 'payment') {
    return (
      <PaymentPage user={user} onBack={() => setCurrentPage('profile')} />
    );
  }

  if (currentPage === 'tariff') {
    return (
      <TariffSelectionPage
        onBack={() => setCurrentPage('home')}
        onNext={(tariffId) => {
          setSubscribeTariff(tariffId);
          setCurrentPage('subscription');
        }}
      />
    );
  }

  if (currentPage === 'subscription') {
    const subscribeUserId = user?.userid ?? user?.id;
    // Pass trial end date so the confirmation modal shows the correct "active until" date
    const _isTrial = ['trial', 'триал'].includes((user?.tariff_plan || '').toLowerCase());
    const trialEndDate = _isTrial && user?.tariff_end_date ? user.tariff_end_date : null;
    return (
      <SubscriptionSelectionPage
        onBack={() => {
          setSubscribeTariff(null);
          setCurrentPage('home');
        }}
        tariffId={subscribeTariff}
        userId={subscribeUserId}
        onOpenReferral={() => setIsHomeReferralOpen(true)}
        trialEndDate={trialEndDate}
      />
    );
  }

  if (currentPage === 'setup') {
    return (
      <SetupFlow 
        onBack={() => setCurrentPage('home')} 
        onComplete={() => setCurrentPage('home')}
        user={user}
        onNavigateToBuy={() => { setSubscribeTariff('pro'); setCurrentPage('subscription'); }}
      />
    );
  }

  if (currentPage === 'success') {
    const inTelegram = isInsideTelegram();

    const handleGoHome = () => {
      setPaymentReturnStatus(null);
      if (user?.userid) {
        apiService.getUser(user.userid).then(setUser).finally(() => setCurrentPage('home'));
      } else {
        setCurrentPage('home');
      }
      window.history.replaceState({}, '', window.location.origin + '/');
    };

    return (
      <PaymentReturnPage
        status={paymentReturnStatus}
        onGoHome={handleGoHome}
        showTelegramLink={!inTelegram}
        miniAppLink={miniAppLink}
      />
    );
  }

  const hasActiveSubscription = Boolean(
    user?.tariff_end_date && new Date(user.tariff_end_date) > new Date()
  );

  // Trial users can still buy a paid subscription — remaining trial days are summed with new sub on backend.
  const TRIAL_PLAN_NAMES = ['trial', 'триал'];
  const isTrialUser = TRIAL_PLAN_NAMES.includes((user?.tariff_plan || '').toLowerCase());
  const hasPaidActiveSubscription = hasActiveSubscription && !isTrialUser;

  const handleLogoClick = () => {
    if (hasPaidActiveSubscription) {
      setCurrentPage('setup');
      return;
    }
    setSubscribeTariff('pro');
    setCurrentPage('subscription');
  };

  return (
      <div className="app">
        <div className="background"></div>
        <AppHeader title="Rocket VPN" />
        <div className="content">
          <Logo onClick={handleLogoClick} isClickable />
          <div className="buttons-wrapper">
            <div className="buttons-container">
              <ServiceStatus user={user} />
              {/* Buy/extend button visible for all users, including active paid subscribers */}
              <SubscriptionButton user={user} onOpenSubscribe={() => { setSubscribeTariff('pro'); setCurrentPage('subscription'); }} isPrimary={!hasPaidActiveSubscription} />
              <SetupButton onOpenSetup={() => { setShowInstallHint(false); setCurrentPage('setup'); }} isPrimary={hasActiveSubscription} />
              <HomeReferralButton onOpenReferral={() => {
                const uid = user?.userid ?? user?.id;
                if (uid) apiService.trackEvent({ funnel_id: 4, step: 'invite_button_clicked', user_id: uid });
                setIsHomeReferralOpen(true);
              }} />
              <div className="bottom-buttons">
                <ProfileButton onOpenProfile={() => setCurrentPage('profile')} />
                <SupportButton />
              </div>
            </div>
          </div>
        </div>
        {showInstallHint && (
          <div className="install-hint-backdrop">
            <div className="install-hint-floating">
              <div className="install-hint-bubble">
                Сначала нужно настроить VPN на вашем устройстве
                <span className="install-hint-arrow" />
              </div>
              <SetupButton onOpenSetup={() => { setShowInstallHint(false); setCurrentPage('setup'); }} isPrimary={hasActiveSubscription} />
            </div>
          </div>
        )}
        <ReferralModal
          isOpen={isHomeReferralOpen}
          onClose={() => setIsHomeReferralOpen(false)}
          referralLink={referralLink}
          friendsInvited={friendsInvited}
          rewards={referralRewards}
          isLoading={isReferralLoading}
        />
      </div>
  );
}

export default App;

